import { computed, inject } from '@angular/core';
import {
  signalStore,
  withComputed,
  withMethods,
  withState,
  patchState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { encodeApiError, TApiErrorResponse } from '@sandbox-platform/shared-ui';

import { CommissionSettingsApiService } from '../../../../services/commission-settings-api.service';
import {
  CommissionSettingsDTO,
  UpdateCommissionSettingsDTO,
} from '../../../../types/commission-settings.types';
import { CacheStore } from '../../../../store/cache.store';

interface CommissionSettingsDetailState {
  settings: CommissionSettingsDTO | null;
  loading: boolean;
  error: string | TApiErrorResponse | null;
  hasChanges: boolean;
}

const initialState: CommissionSettingsDetailState = {
  settings: null,
  loading: false,
  error: null,
  hasChanges: false,
};

export const CommissionSettingsDetailStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    hasSettings: computed(() => store.settings() !== null),
    isLoading: computed(() => store.loading()),
    hasPersistedSettings: computed(() => (store.settings()?.version ?? 0) > 0),
  })),

  withMethods((store, apiService = inject(CommissionSettingsApiService), cacheStore = inject(CacheStore)) => ({
    async get(): Promise<CommissionSettingsDTO | null> {
      patchState(store, { loading: true, error: null });

      try {
        const response = await firstValueFrom(apiService.get('detail'));
        if (response?.data) {
          patchState(store, { settings: response.data, hasChanges: false });
          return response.data;
        }

        patchState(store, { error: 'No commission settings data received' });
        return null;
      } catch (error) {
        patchState(store, {
          error: encodeApiError(error, 'Failed to load commission settings'),
        });
        return null;
      } finally {
        patchState(store, { loading: false });
      }
    },

    async update(dto: UpdateCommissionSettingsDTO): Promise<CommissionSettingsDTO | null> {
      patchState(store, { loading: true, error: null });

      try {
        const response = await firstValueFrom(apiService.update(dto));
        if (response?.data) {
          patchState(store, { settings: response.data, hasChanges: false });
          cacheStore.setCommissionSettings(response.data);
          return response.data;
        }

        patchState(store, { error: 'No commission settings data received' });
        return null;
      } catch (error) {
        patchState(store, {
          error: encodeApiError(error, 'Failed to save commission settings'),
        });
        return null;
      } finally {
        patchState(store, { loading: false });
      }
    },

    setSettings(settings: CommissionSettingsDTO | null): void {
      patchState(store, { settings, hasChanges: false });
    },

    markChanged(): void {
      patchState(store, { hasChanges: true });
    },

    clearError(): void {
      patchState(store, { error: null });
    },
  }))
);

export type CommissionSettingsDetailStoreType = InstanceType<
  typeof CommissionSettingsDetailStore
>;
